import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ChevronRight } from "@material-ui/icons";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BackDropLoading from "../../../../pcmp/components/common/models/BackDrop";
import {
  fetchAllForecastScheduleApprovalRoles,
  updateDeliveryScheduleForecast,
} from "../../../containers/actions/common/forecastScheduleActions";
import { useDispatch, useSelector } from "react-redux";
import usePrevious from "../../../helpers/previousProps";
import { FETCH_ALL_FORECAST_SCHEDULE_APPROVAL_ROLES_LOADING } from "../../../containers/actions/types";

const useStyles = makeStyles((theme) => ({
  titleGrid: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // padding: '1rem',
  },
  head: {
    textAlign: "center",
  },
  closeBtn: {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: "12px",
    right: "10px",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      color: "#FF0000",
    },
  },
  requiredStar: {
    color: "red",
  },
  cancelBtn: {
    border: "1px solid #000000de",
    backgroundColor: "white",
    color: "black",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "black",
      color: "white",
      border: "1px solid #000000de",
    },
  },
  checkboxContainer: {
    display: "flex",
    flexDirection: "column", // Display checkboxes vertically
    alignItems: "flex-start", // Align items to the left
  },
  checkboxLabel: {
    fontSize: "14px",
    fontWeight: "500",
  },
  dialogContent: {
    textAlign: "center",
    padding: "24px 36px 24px 36px",
  },
  avatar: {
    color: "green",
    margin: "0 auto",
    fontSize: "54px",
  },
  dialogActions: {
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
}));

const FinalizedSchedule = (props) => {
  const {
    closeFinalizedSchedulePopup,
    openDialog,
    setFinalizedSchedulePopup,
    finalizedSchedulePopup,
    schedule,
    dsForecastDetails,
    handleUpdateSuccess,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [checkedRoles, setCheckedRoles] = useState({});
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);

  const finalizedApprovalRoles = useSelector(
    (state) => state.pcmp.common.forecastSchedule.forecastScheduleApprovalRoles
  );

  const forecastScheduleApprovalRolesLoading = useSelector(
    (state) =>
      state.pcmp.common.forecastSchedule.forecastScheduleApprovalRolesLoading
  );

  useEffect(() => {
    dispatch({
      type: FETCH_ALL_FORECAST_SCHEDULE_APPROVAL_ROLES_LOADING,
      payload: true,
    });
    dispatch(fetchAllForecastScheduleApprovalRoles());
  }, []);

  useEffect(() => {
    if (finalizedApprovalRoles && finalizedApprovalRoles.length > 0) {
      const initialChecked = finalizedApprovalRoles.reduce((acc, role) => {
        acc[role.collaborator_role_id] = false; // Initialize all roles as unchecked
        return acc;
      }, {});
      setCheckedRoles(initialChecked);
    }
  }, [finalizedApprovalRoles]);

  // const handleCheckboxChange = (roleId) => {
  //   setCheckedRoles((prevState) => ({
  //     ...prevState,
  //     [roleId]: !prevState[roleId],
  //   }));
  // };

  // Function to handle checkbox change
  const handleCheckboxChange = (roleId) => {
    const newCheckedRoles = { ...checkedRoles };

    // Get the current index of the roleId being toggled
    const currentRoleIndex = finalizedApprovalRoles.findIndex(
      (role) => role.collaborator_role_id === roleId
    );

    // Check if the checkbox is currently checked or unchecked
    const isChecked = checkedRoles[roleId];

    if (isChecked) {
      // Uncheck the highest-indexed checkbox, keep previous ones checked
      for (let i = currentRoleIndex; i < finalizedApprovalRoles.length; i++) {
        const currentRoleId = finalizedApprovalRoles[i].collaborator_role_id;
        newCheckedRoles[currentRoleId] = false; // Uncheck the current and all roles below it
      }
    } else {
      // Check all roles from the top to the current one
      for (let i = 0; i <= currentRoleIndex; i++) {
        const currentRoleId = finalizedApprovalRoles[i].collaborator_role_id;
        newCheckedRoles[currentRoleId] = true; // Set checked to true for all checkboxes above and including this one
      }
    }

    setCheckedRoles(newCheckedRoles); // Update the state with the new checked roles
  };

  // Function to close the Save Changes popup
  const handleSaveChanges = () => {
    closeFinalizedSchedulePopup();
  };

  const isSaveButtonDisabled = () => {
    return !Object.values(checkedRoles).some((isChecked) => isChecked);
  };

  const handleFinalizeSchedule = () => {
    const requestObject = {
      status: schedule.status,
      sponsorOrgID: schedule.sponsorOrgID,
      cdmoOrgID: schedule.cdmoOrgID,
      approvalAccessRolesArray: Object.keys(checkedRoles)
        .filter((key) => checkedRoles[key]) // Filter only keys with true value
        .map(Number),
      collabrixForecastScheduleID: schedule.collabrixForecastScheduleID,
      scheduleAcceptedFlag: true,
      scheduleFinalizedFlag: true,
      deliveryScheduleForecastDetailsList:
        dsForecastDetails.deliveryScheduleForecastDetailsList,
    };

    // Log the final request object
    // console.log("Request object to be sent:", requestObject);
    dispatch(updateDeliveryScheduleForecast(requestObject));
  };

  const errRef = useRef();
  const scrollToTop = () => {
    setTimeout(
      () => errRef.current.scrollIntoView({ behavior: "smooth", block: "end" }),
      100
    );
  };

  const [updateError, setUpdateError] = useState(false);
  const [updatedData, setUpdatedData] = useState({});
  const didMountRef = useRef(false);

  const updateDeliveryScheduleForecastData = useSelector(
    (state) =>
      state.pcmp.common.forecastSchedule?.updateDeliveryScheduleForecastData ||
      {}
  );
  const preUpdateDeliveryScheduleForecastData = usePrevious(
    updateDeliveryScheduleForecastData
  );

  useEffect(() => {
    if (didMountRef.current) {
      if (
        JSON.stringify(updateDeliveryScheduleForecastData) !==
        JSON.stringify(preUpdateDeliveryScheduleForecastData)
      ) {
        if (updateDeliveryScheduleForecastData.status === true) {
          // setLoading(false);
          setUpdateError(false);
          setUpdatedData(updateDeliveryScheduleForecastData);
          handleUpdateSuccess("Approval Structure Saved Succesfully");
          //   setOpenSuccessDialog(true);
        } else if (updateDeliveryScheduleForecastData.status === false) {
          // setLoading(false);
          setUpdateError(true);
          //   scrollToTop();
          setUpdatedData(updateDeliveryScheduleForecastData);
          //   setOpenSuccessDialog(false);
        }
      }
    } else {
      didMountRef.current = true;
    }
  }, [
    updateDeliveryScheduleForecastData,
    preUpdateDeliveryScheduleForecastData,
  ]);

  return (
    <>
      <BackDropLoading />
      <Dialog
        open={openDialog}
        onClose={closeFinalizedSchedulePopup}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Grid item xs={12} className={classes.header}>
            <Grid item xs={12} className={classes.titleGrid}>
              <Grid item xs={12} className={classes.head}>
                <Typography
                  variant="h6"
                  style={{
                    fontSize: "1.4rem",
                    fontWeight: "bold",
                    textAlign: "start",
                  }}
                >
                  Finalized Schedule - Approvals
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={2} sm={2} md={2}>
              <Typography
                variant="body2"
                className={classes.closeBtn}
                onClick={closeFinalizedSchedulePopup}
              >
                Cancel{" "}
                <CloseIcon style={{ fontSize: "35px", color: "#FF0000" }} />
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {forecastScheduleApprovalRolesLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center", // Horizontally center the CircularProgress
                alignItems: "center", // Vertically center the CircularProgress
                height: "100px", // Optional: Set a height to vertically center the loader
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Grid
              style={{
                border: "2px solid #D3D3D3",
                boxShadow: "0 4px 2px -2px rgba(0, 0, 0, 0.2)",
                paddingLeft: "20px",
                marginBottom: "2rem",
              }}
            >
              <Typography
                style={{
                  fontSize: "0.9rem",
                  marginTop: "20px",
                  color: "black",
                  fontWeight: "bold",
                }}
                gutterBottom
              >
                Select appropriate structure for approval of Finalized Schedule
                from Following Options:
                <span className={classes.requiredStar}> *</span>
              </Typography>
              <Grid className={classes.checkboxContainer}>
                {finalizedApprovalRoles?.map((role) => (
                  <FormControlLabel
                    key={role.collaborator_role_id}
                    control={
                      <Checkbox
                        checked={
                          checkedRoles[role.collaborator_role_id] || false
                        }
                        onChange={() =>
                          handleCheckboxChange(role.collaborator_role_id)
                        }
                        color="primary"
                        disableRipple // Disable click effect
                      />
                    }
                    // label={role.role_name}
                    label={`${role.role_name} approval`}
                    className={classes.checkboxLabel}
                  />
                ))}
              </Grid>

              <DialogActions
                style={{
                  display: "flex",
                  justifyContent: "start",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ChevronRight />}
                  style={{
                    marginBottom: "1rem",
                    borderRadius: "20px",
                    textTransform: "none",
                  }}
                  onClick={handleFinalizeSchedule}
                  disabled={isSaveButtonDisabled()}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {openFinalizeDialog && (
        <Dialog open={openFinalizeDialog} maxWidth="sm" fullWidth>
          <DialogContent className={classes.dialogContent}>
            <>
              <CheckCircleIcon className={classes.avatar} />

              <Typography
                variant="h6"
                style={{
                  marginTop: "10px",
                  fontWeight: "bold",
                  color: "black",
                }}
              >
                Approval Structure Saved Successfully
              </Typography>
              <DialogActions className={classes.dialogActions}>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ChevronRight />}
                  style={{
                    borderRadius: "20px",
                    textTransform: "none",
                  }}
                  onClick={handleSaveChanges}
                >
                  Ok
                </Button>
              </DialogActions>
            </>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default FinalizedSchedule;
